import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { cleanupExpiredDrafts, getAllPendingDrafts } from '@/hooks/useLocalDraft';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

const DEBUG = import.meta.env.DEV;

const log = (emoji: string, message: string, data?: unknown) => {
  if (!DEBUG) return;
  const style = 'color: #10b981; font-weight: bold;';
  if (data) {
    console.log(`%c[DraftSyncManager] ${emoji} ${message}`, style, data);
  } else {
    console.log(`%c[DraftSyncManager] ${emoji} ${message}`, style);
  }
};

/**
 * App-level component that manages draft synchronization and cleanup.
 * Runs on app startup to:
 * 1. Clean up expired drafts from localStorage
 * 2. Sync any pending drafts when coming back online
 */
export function DraftSyncManager() {
  const { user } = useAuth();
  const hasCheckedOnMount = useRef(false);
  const isSyncingRef = useRef(false);

  // Clean up expired drafts on mount
  useEffect(() => {
    cleanupExpiredDrafts();
    log('🧹', 'Cleaned up expired drafts');
  }, []);

  const attemptSync = useCallback(async (showToast = true): Promise<boolean> => {
    if (!user || isSyncingRef.current) {
      log('⏳', 'Sync skipped', { hasUser: !!user, isSyncing: isSyncingRef.current });
      return false;
    }

    const pendingDrafts = getAllPendingDrafts();
    if (pendingDrafts.length === 0) {
      log('📭', 'No pending drafts to sync');
      return false;
    }

    isSyncingRef.current = true;
    log('🔄', 'Attempting sync', { count: pendingDrafts.length });

    let syncedCount = 0;

    try {
      for (const draft of pendingDrafts) {
        try {
          if (draft.entityType === 'section') {
            const { error } = await supabase
              .from('sections')
              .update({ 
                content: draft.content,
                title: draft.title,
                updated_at: new Date().toISOString()
              })
              .eq('id', draft.entityId);
            
            if (!error) {
              syncedCount++;
              // Clear from localStorage after successful sync
              const key = `atlasdraft-draft-${draft.entityType}-${draft.entityId}`;
              localStorage.removeItem(key);
            } else {
              log('❌', 'Failed to sync draft', { error, entityId: draft.entityId });
            }
          }
        } catch (err) {
          log('❌', 'Error syncing draft', { err, entityId: draft.entityId });
        }
      }

      if (syncedCount > 0 && showToast) {
        log('✅', 'Sync completed', { syncedCount });
        toast.success('Synced offline changes', {
          description: `${syncedCount} draft(s) saved successfully`
        });
      }

      return syncedCount > 0;
    } finally {
      isSyncingRef.current = false;
    }
  }, [user]);

  // Check for pending drafts on mount
  useEffect(() => {
    if (hasCheckedOnMount.current || !user) return;
    hasCheckedOnMount.current = true;

    const pendingDrafts = getAllPendingDrafts();
    log('🚀', 'Mount check', { 
      hasPendingDrafts: pendingDrafts.length > 0, 
      isOnline: navigator.onLine,
      count: pendingDrafts.length 
    });

    if (pendingDrafts.length > 0 && navigator.onLine) {
      setTimeout(() => {
        log('⏰', 'Delayed sync triggered after mount');
        attemptSync(true);
      }, 1000);
    }
  }, [user, attemptSync]);

  // Listen for online event to auto-sync
  useEffect(() => {
    const handleOnline = () => {
      log('🌐', 'Online event detected');
      const pendingDrafts = getAllPendingDrafts();
      if (pendingDrafts.length > 0) {
        log('📬', 'Found pending drafts, scheduling sync', { count: pendingDrafts.length });
        setTimeout(() => {
          attemptSync(true);
        }, 500);
      }
    };

    window.addEventListener('online', handleOnline);
    log('👂', 'Listening for online events');
    
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [attemptSync]);

  // Expose sync function globally for OfflineBanner to use
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__draftSyncManager = {
      attemptSync,
      hasPendingDraft: () => getAllPendingDrafts().length > 0,
    };

    return () => {
      delete (window as unknown as Record<string, unknown>).__draftSyncManager;
    };
  }, [attemptSync]);

  return null;
}
